/**
 * WebSocket hook for Agent Chat streaming.
 * 
 * Connects to: <configured WS base>/ws/agents/<agent_id>/chat/
 * 
 * This hook provides real-time streaming for agent executions,
 * including tool calls, LLM responses, and execution status updates.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../services/auth';
import { WS_BASE_URL } from '../api/config';

const RECOVERABLE_EXECUTION_STATUSES = new Set(['RUNNING', 'QUEUED', 'PENDING_CONFIRMATION']);
const TERMINAL_EXECUTION_STATUSES = new Set(['COMPLETED', 'ERROR', 'CANCELLED', 'FAILED', 'STOPPED']);

// Message types from server
export interface AgentSocketMessage {
  type: string;
  [key: string]: any;
}

export interface ExecutionStartedMessage {
  type: 'execution_started';
  execution_id: string;
  session_id: string;
}

export interface ExecutionQueuedMessage {
  type: 'execution_queued';
  task_id: string;
  queue: string;
  session_id?: string | null;
}

export interface IterationStartedMessage {
  type: 'iteration_started';
  iteration: number;
}

export interface ToolCallMessage {
  type: 'tool_call';
  tool_name: string;
  tool_args: Record<string, any>;
  tool_call_id?: string;
  tool_rationale?: string;
  tool_group_rationale?: string;
  iteration: number;
}

export interface ToolResultMessage {
  type: 'tool_result';
  tool_name: string;
  result: any;
  iteration: number;
}

export interface LLMResponseMessage {
  type: 'llm_response';
  content: string;
  iteration: number;
  is_final?: boolean;
}

export interface AssistantStatusMessage {
  type: 'assistant_status';
  content: string;
  iteration: number;
  execution_id?: string;
  session_id?: string;
  tool_names?: string[];
  tool_group_rationale?: string;
  tool_rationales?: Array<Record<string, any>>;
}

export interface ExecutionCompletedMessage {
  type: 'execution_completed';
  execution_id: string;
  session_id: string;
  status: string;
  result: any;
  total_cost?: number;
  total_iterations?: number;
}

export interface ExecutionErrorMessage {
  type: 'execution_error';
  error: string;
  execution_id?: string;
}

export interface PendingConfirmationMessage {
  type: 'pending_confirmation';
  execution_id: string;
  tool_name: string;
  tool_args: Record<string, any>;
  tool_call_id: string;
  danger_level: number;
  message: string;
}

export interface ConfirmationResolvedMessage {
  type: 'confirmation_resolved';
  tool_name: string;
  confirmed: boolean;
  reason?: string;
}

export interface UseAgentChatSocketOptions {
  /** Agent ID to connect to */
  agentId: number;
  /** Whether to auto-connect on mount */
  autoConnect?: boolean;
  /** Callback when connected */
  onConnected?: (agentName: string) => void;
  /** Callback when disconnected */
  onDisconnected?: () => void;
  /** Callback when reconnected after a disconnect - use to sync data from API */
  onReconnected?: (wasExecuting: boolean, executionId: string | null) => void;
  /** Callback when subscribed to an execution (for page reload scenarios) */
  onSubscribed?: (executionId: string, status: string, isStreaming: boolean) => void;
  /** Callback when execution starts */
  onExecutionStarted?: (executionId: string, sessionId: string) => void;
  /** Callback when execution is accepted by the server but still queued */
  onExecutionQueued?: (taskId: string, sessionId?: string | null) => void;
  /** Callback for iteration progress */
  onIterationStarted?: (iteration: number, maxIterations: number) => void;
  /** Callback for tool calls. `toolCallId` is forwarded so consumers can
   *  anchor nested sub-agent events to the right parent. */
  onToolCall?: (
    toolName: string,
    toolArgs: Record<string, any>,
    iteration: number,
    toolCallId?: string,
    data?: AgentSocketMessage
  ) => void;
  /** Callback for tool results */
  onToolResult?: (
    toolName: string,
    result: any,
    iteration: number,
    toolCallId?: string
  ) => void;
  /**
   * Callback fired for every nested sub-agent event (source === 'sub_agent').
   * Consumers use this to attach the event to the matching parent tool_call
   * message via `attachSubAgentEvent`. When this callback is provided, the
   * hook does NOT fire the normal onToolCall / onToolResult / onLLMResponse
   * callbacks for sub-agent events so they don't surface as top-level UI.
  */
  onSubAgentEvent?: (event: any) => void;
  /** Callback for public assistant progress/status text */
  onAssistantStatus?: (
    content: string,
    iteration: number,
    data: AssistantStatusMessage
  ) => void;
  /** Callback for LLM responses */
  onLLMResponse?: (content: string, iteration: number, isFinal: boolean) => void;
  /** Callback when execution completes */
  onExecutionCompleted?: (executionId: string, status: string, result: any) => void;
  /** Callback for execution data updates (tokens, cost) - fires when tools start AND finish */
  onExecutionData?: (data: { totalTokens: number; totalCost: number; iteration: number; toolCallsStarting?: string[] | null }) => void;
  /** Callback for errors */
  onError?: (error: string) => void;
  /** Callback when a tool requires user confirmation (dangerous operation) */
  onPendingConfirmation?: (data: {
    executionId: string;
    toolName: string;
    toolArgs: Record<string, any>;
    toolCallId: string;
    dangerLevel: number;
    message: string;
  }) => void;
  /** Callback when a confirmation is resolved (confirmed/denied/timed out) */
  onConfirmationResolved?: (toolName: string, confirmed: boolean, reason?: string) => void;
}

export interface UseAgentChatSocketReturn {
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Whether currently executing */
  isExecuting: boolean;
  /** Current execution ID */
  executionId: string | null;
  /** Current queued Celery task ID before an execution row exists */
  queuedTaskId: string | null;
  /** Current session ID */
  sessionId: string | null;
  /** Current iteration number */
  currentIteration: number;
  /** Max iterations */
  maxIterations: number;
  /** Execution status */
  status: string | null;
  /** Last error */
  error: string | null;
  /** Execution result */
  result: any | null;
  /** Total cost */
  totalCost: number;
  /** Total tokens */
  totalTokens: number;
  /** Tool names loaded for this agent */
  toolNames: string[];
  /** Direct reference to the WebSocket - use this for checking actual connection state */
  wsRef: React.MutableRefObject<WebSocket | null>;
  /** Connect to WebSocket */
  connect: () => void;
  /** Disconnect from WebSocket */
  disconnect: () => void;
  /** Execute a message */
  execute: (message: string, options?: {
    sessionId?: string | null;
    aiSystemId?: number;
    context?: Record<string, any>;
    maxIterations?: number;
  }) => void;
  /** Stop current execution - optionally pass executionId to avoid stale closure */
  stop: (executionId?: string) => boolean;
  /** Reset state */
  reset: () => void;
  /** Force stop execution state (for when WebSocket doesn't respond) */
  forceStopState: () => void;
  /** Restore execution state (for page reload scenarios) */
  restore: (executionId: string, sessionId: string | null) => void;
  /** Reset reconnect counter and connect (useful after max attempts reached) */
  resetAndConnect: () => void;
  /** Send a raw message directly via WebSocket */
  sendRaw: (payload: Record<string, any>) => boolean;
  /** Confirm or deny a dangerous tool execution */
  confirmTool: (
    executionId: string,
    toolName: string,
    confirmed: boolean,
    toolCallId?: string | null
  ) => boolean;
  /** Pending confirmation data (null if no confirmation pending) */
  pendingConfirmation: {
    executionId: string;
    toolName: string;
    toolArgs: Record<string, any>;
    toolCallId: string;
    dangerLevel: number;
    message: string;
  } | null;
}

export function useAgentChatSocket(options: UseAgentChatSocketOptions): UseAgentChatSocketReturn {
  const {
    agentId,
    autoConnect = false,
    onConnected,
    onDisconnected,
    onReconnected,
    onSubscribed,
    onExecutionStarted,
    onExecutionQueued,
    onIterationStarted,
    onToolCall,
    onToolResult,
    onAssistantStatus,
    onLLMResponse,
    onSubAgentEvent,
    onExecutionCompleted,
    onExecutionData,
    onError,
    onPendingConfirmation,
    onConfirmationResolved,
  } = options;

  // WebSocket ref
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const wasConnectedRef = useRef(false); // Track if this is a reconnection
  const executingAtDisconnectRef = useRef<{ wasExecuting: boolean; executionId: string | null }>({ wasExecuting: false, executionId: null });
  const handleMessageRef = useRef<((event: MessageEvent) => void) | null>(null);
  const executionIdRef = useRef<string | null>(null);
  const queuedTaskIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const isExecutingRef = useRef(false);
  const statusRef = useRef<string | null>(null);
  const stopPendingExecutionIdRef = useRef<string | null>(null);
  const stopPendingTaskIdRef = useRef<string | null>(null);
  const ignoredExecutionIdsRef = useRef<Set<string>>(new Set());
  const terminalExecutionIdsRef = useRef<Set<string>>(new Set());
  const maxReconnectAttempts = 5;

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [queuedTaskId, setQueuedTaskId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentIteration, setCurrentIteration] = useState(0);
  const [maxIterations, setMaxIterations] = useState(100);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [totalCost, setTotalCost] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [toolNames, setToolNames] = useState<string[]>([]);
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    executionId: string;
    toolName: string;
    toolArgs: Record<string, any>;
    toolCallId: string;
    dangerLevel: number;
    message: string;
  } | null>(null);

  useEffect(() => {
    executionIdRef.current = executionId;
  }, [executionId]);

  useEffect(() => {
    queuedTaskIdRef.current = queuedTaskId;
  }, [queuedTaskId]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    isExecutingRef.current = isExecuting;
  }, [isExecuting]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const hasRecoverableExecution = useCallback(() => {
    const currentStatus = statusRef.current ? String(statusRef.current).toUpperCase() : null;
    if (currentStatus && TERMINAL_EXECUTION_STATUSES.has(currentStatus)) {
      return false;
    }
    return Boolean(
      isExecutingRef.current ||
      executionIdRef.current ||
      queuedTaskIdRef.current ||
      (currentStatus && RECOVERABLE_EXECUTION_STATUSES.has(currentStatus))
    );
  }, []);

  const getMessageExecutionId = (data: AgentSocketMessage): string | null => {
    const rawExecutionId = data.execution_id || data.executionId;
    return rawExecutionId ? String(rawExecutionId) : null;
  };

  const isTerminalMessage = (type: string) => (
    type === 'execution_completed' ||
    type === 'execution_error' ||
    type === 'execution_stopped'
  );

  const shouldIgnoreLateStopActivity = (data: AgentSocketMessage): boolean => {
    const pendingStopId = stopPendingExecutionIdRef.current;
    if (!pendingStopId) return false;

    if (
      data.type === 'execution_started' ||
      data.type === 'stop_requested' ||
      data.type === 'subscribed' ||
      data.type === 'connected' ||
      isTerminalMessage(data.type)
    ) {
      return false;
    }

    const eventExecutionId = getMessageExecutionId(data);
    return !eventExecutionId || eventExecutionId === pendingStopId;
  };

  // Build WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const token = authService.getAccessToken();
    if (!token) {
      console.error('[AgentChatSocket] No auth token available');
      return null;
    }

    if (!WS_BASE_URL) {
      console.error('[AgentChatSocket] No WebSocket base URL available');
      return null;
    }

    return `${WS_BASE_URL}/ws/agents/${agentId}/chat/?token=${encodeURIComponent(token)}`;
  }, [agentId]);

  // Handle incoming messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data: AgentSocketMessage = JSON.parse(event.data);
      console.log('[AgentChatSocket] Received:', data.type, data);

      const eventExecutionId = getMessageExecutionId(data);
      const eventTaskId = data.task_id ? String(data.task_id) : null;
      const terminalEventExecutionId = isTerminalMessage(data.type)
        ? eventExecutionId || executionIdRef.current || stopPendingExecutionIdRef.current
        : eventExecutionId;

      if (terminalEventExecutionId && terminalExecutionIdsRef.current.has(terminalEventExecutionId)) {
        console.log('[AgentChatSocket] Ignoring duplicate terminal event for:', terminalEventExecutionId);
        return;
      }

      if (eventExecutionId && ignoredExecutionIdsRef.current.has(eventExecutionId)) {
        console.log('[AgentChatSocket] Ignoring stale event for reset execution:', eventExecutionId);
        return;
      }

      if (shouldIgnoreLateStopActivity(data)) {
        console.log('[AgentChatSocket] Ignoring late activity after stop:', data.type);
        return;
      }

      if (data.type === 'connected' && data.running_execution?.execution_id) {
        const runningExecutionId = String(data.running_execution.execution_id);
        if (ignoredExecutionIdsRef.current.has(runningExecutionId)) {
          console.log('[AgentChatSocket] Ignoring stale running execution on connect:', runningExecutionId);
          data.running_execution = null;
        }
      }

      // Nested sub-agent events are routed to `onSubAgentEvent` so consumers
      // can render them nested under the matching parent tool_call instead
      // of adding them as new top-level messages.
      if (data && (data as any).source === 'sub_agent' && (data as any).parent_tool_call_id) {
        console.log(
          '[AgentChatSocket] SUB-AGENT event →',
          data.type,
          'parent:', (data as any).parent_tool_call_id,
          'sub:', (data as any).sub_agent_name,
        );
        onSubAgentEvent?.(data);
        return;
      }

      switch (data.type) {
        case 'connected':
          console.log('[AgentChatSocket] Connected to agent:', data.agent_name);
          // Check if the backend reports a running execution (auto-subscribed on reconnect)
          if (data.running_execution) {
            const re = data.running_execution;
            console.log('[AgentChatSocket] Backend auto-subscribed to running execution:', re.execution_id);
            const runningExecutionId = String(re.execution_id);
            executionIdRef.current = runningExecutionId;
            setExecutionId(runningExecutionId);
            isExecutingRef.current = true;
            setIsExecuting(true);
            statusRef.current = 'RUNNING';
            setStatus('RUNNING');
            setError(null);
            onSubscribed?.(runningExecutionId, 'RUNNING', true);
          }
          onConnected?.(data.agent_name);
          break;

        case 'session_started':
          // Session created/resumed - update session ID
          console.log('[AgentChatSocket] Session started:', data.session_id);
          sessionIdRef.current = data.session_id;
          setSessionId(data.session_id);
          break;

        case 'skills_loaded':
          console.log('[AgentChatSocket] Skills loaded:', data.active_skills || []);
          break;

        case 'tools_loaded':
          // Tools loaded notification with tool names
          console.log('[AgentChatSocket] Tools loaded:', data.tool_count, data.tool_names);
          setToolNames(data.tool_names || []);
          break;

        case 'execution_started':
          ignoredExecutionIdsRef.current.delete(data.execution_id);
          terminalExecutionIdsRef.current.delete(data.execution_id);
          stopPendingExecutionIdRef.current = null;
          stopPendingTaskIdRef.current = null;
          executionIdRef.current = data.execution_id;
          setExecutionId(data.execution_id);
          queuedTaskIdRef.current = null;
          setQueuedTaskId(null);
          sessionIdRef.current = data.session_id;
          setSessionId(data.session_id);
          isExecutingRef.current = true;
          setIsExecuting(true);
          statusRef.current = 'RUNNING';
          setStatus('RUNNING');
          setError(null);
          setCurrentIteration(0);
          setTotalTokens(0);
          setTotalCost(0);
          onExecutionStarted?.(data.execution_id, data.session_id);
          break;

        case 'execution_queued':
          console.log('[AgentChatSocket] Execution queued:', data.task_id, data.queue);
          queuedTaskIdRef.current = data.task_id;
          setQueuedTaskId(data.task_id);
          isExecutingRef.current = true;
          setIsExecuting(true);
          statusRef.current = 'QUEUED';
          setStatus('QUEUED');
          setError(null);
          if (data.session_id) {
            sessionIdRef.current = data.session_id;
            setSessionId(data.session_id);
          }
          onExecutionQueued?.(data.task_id, data.session_id ?? null);
          break;

        case 'iteration_started':
          setCurrentIteration(data.iteration);
          if (data.max_iterations) setMaxIterations(data.max_iterations);
          onIterationStarted?.(data.iteration, data.max_iterations || 100);
          break;

        case 'llm_thinking':
          // LLM is thinking - could show a spinner or status
          console.log('[AgentChatSocket] LLM thinking, iteration:', data.iteration);
          break;

        case 'assistant_status':
          console.log('[AgentChatSocket] Assistant status:', data.content);
          onAssistantStatus?.(
            data.content || '',
            data.iteration || currentIteration,
            data as AssistantStatusMessage
          );
          break;

        case 'tool_call':
          console.log('[AgentChatSocket] Tool call:', data.tool_name);
          onToolCall?.(
            data.tool_name,
            data.tool_args,
            data.iteration || currentIteration,
            data.tool_call_id,
            data
          );
          break;

        case 'tool_result':
          console.log('[AgentChatSocket] Tool result:', data.tool_name);
          onToolResult?.(
            data.tool_name,
            data.result,
            data.iteration || currentIteration,
            data.tool_call_id
          );
          break;

        case 'llm_response':
          onLLMResponse?.(data.content, data.iteration, data.is_final || false);
          break;

        case 'execution_data':
          // Real-time execution metrics update (fires both when tools start AND finish)
          if (data.total_tokens) setTotalTokens(data.total_tokens);
          if (data.total_cost) setTotalCost(data.total_cost);
          setCurrentIteration(data.iteration);
          onExecutionData?.({
            totalTokens: data.total_tokens || 0,
            totalCost: data.total_cost || 0,
            iteration: data.iteration,
            toolCallsStarting: data.tool_calls_starting || null,
          });
          break;

        case 'execution_completed':
          if (terminalEventExecutionId) {
            terminalExecutionIdsRef.current.add(terminalEventExecutionId);
          }
          if (eventExecutionId && stopPendingExecutionIdRef.current === eventExecutionId) {
            stopPendingExecutionIdRef.current = null;
          }
          isExecutingRef.current = false;
          setIsExecuting(false);
          queuedTaskIdRef.current = null;
          setQueuedTaskId(null);
          statusRef.current = data.status || 'COMPLETED';
          setStatus(data.status || 'COMPLETED');
          setResult(data.result);
          setPendingConfirmation(null);
          if (data.total_cost) setTotalCost(data.total_cost);
          if (data.total_tokens) setTotalTokens(data.total_tokens);
          if (data.iterations) setCurrentIteration(data.iterations);
          onExecutionCompleted?.(data.execution_id, data.status || 'COMPLETED', data.result);
          break;

        case 'execution_error':
          if (terminalEventExecutionId) {
            terminalExecutionIdsRef.current.add(terminalEventExecutionId);
          }
          if (eventExecutionId && stopPendingExecutionIdRef.current === eventExecutionId) {
            stopPendingExecutionIdRef.current = null;
          }
          isExecutingRef.current = false;
          setIsExecuting(false);
          queuedTaskIdRef.current = null;
          setQueuedTaskId(null);
          statusRef.current = 'ERROR';
          setStatus('ERROR');
          setError(data.error);
          setPendingConfirmation(null);
          onError?.(data.error);
          break;

        case 'execution_stopped':
          // Execution was cancelled by user
          if (terminalEventExecutionId) {
            terminalExecutionIdsRef.current.add(terminalEventExecutionId);
          }
          if (terminalEventExecutionId && stopPendingExecutionIdRef.current === terminalEventExecutionId) {
            stopPendingExecutionIdRef.current = null;
          }
          if (eventTaskId && stopPendingTaskIdRef.current === eventTaskId) {
            stopPendingTaskIdRef.current = null;
          }
          isExecutingRef.current = false;
          setIsExecuting(false);
          queuedTaskIdRef.current = null;
          setQueuedTaskId(null);
          statusRef.current = 'CANCELLED';
          setStatus('CANCELLED');
          setPendingConfirmation(null);
          onExecutionCompleted?.(terminalEventExecutionId || '', 'CANCELLED', null);
          break;

        case 'pending_confirmation': {
          // A dangerous tool requires user confirmation
          console.log('[AgentChatSocket] Tool requires confirmation:', data.tool_name);
          statusRef.current = 'PENDING_CONFIRMATION';
          setStatus('PENDING_CONFIRMATION');
          const confirmData = {
            executionId: data.execution_id,
            toolName: data.tool_name,
            toolArgs: data.tool_args || {},
            toolCallId: data.tool_call_id,
            dangerLevel: data.danger_level || 0,
            message: data.message || `Tool '${data.tool_name}' requires confirmation.`,
          };
          setPendingConfirmation(confirmData);
          onPendingConfirmation?.(confirmData);
          break;
        }

        case 'confirmation_resolved':
          // Confirmation was resolved (confirmed/denied/timed out)
          console.log('[AgentChatSocket] Confirmation resolved:', data.tool_name, 'confirmed:', data.confirmed);
          setPendingConfirmation(null);
          statusRef.current = 'RUNNING';
          setStatus('RUNNING');
          onConfirmationResolved?.(data.tool_name, data.confirmed, data.reason);
          break;

        case 'tool_confirmation_received':
          // Server acknowledged our confirmation/denial
          console.log('[AgentChatSocket] Confirmation acknowledged:', data.tool_name, 'confirmed:', data.confirmed);
          break;

        case 'stop_requested':
          // Stop was acknowledged - execution will stop soon
          console.log('[AgentChatSocket] Stop acknowledged:', data.message);
          break;

        case 'error':
          setError(data.error);
          onError?.(data.error);
          break;

        case 'pong':
          // Heartbeat response
          break;

        case 'subscribed':
          // Successfully subscribed to an existing execution
          console.log('[AgentChatSocket] Subscribed to execution:', data.execution_id, 'status:', data.status, 'streaming:', data.is_streaming);
          executionIdRef.current = data.execution_id;
          setExecutionId(data.execution_id);
          if (data.status === 'RUNNING') {
            isExecutingRef.current = true;
            setIsExecuting(true);
            statusRef.current = 'RUNNING';
            setStatus('RUNNING');
          } else {
            // Execution already finished
            isExecutingRef.current = false;
            setIsExecuting(false);
            statusRef.current = data.status;
            setStatus(data.status);
            onExecutionCompleted?.(data.execution_id, data.status, null);
          }
          // Notify parent about subscription result
          onSubscribed?.(data.execution_id, data.status, data.is_streaming || false);
          break;

        default:
          console.log('[AgentChatSocket] Unknown message type:', data.type);
      }
    } catch (e) {
      console.error('[AgentChatSocket] Failed to parse message:', e);
    }
  }, [onConnected, onSubscribed, onExecutionStarted, onExecutionQueued, onIterationStarted, onToolCall, onToolResult, onAssistantStatus, onLLMResponse, onExecutionCompleted, onError, onPendingConfirmation, onConfirmationResolved, onSubAgentEvent]);

  // Keep handleMessageRef updated to avoid stale closures in WebSocket handler
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Prevent duplicate connections
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[AgentChatSocket] Already connected or connecting');
      return;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts && !hasRecoverableExecution()) {
      console.log('[AgentChatSocket] Max reconnect attempts reached');
      return;
    }

    const url = getWebSocketUrl();
    if (!url) {
      console.error('[AgentChatSocket] Cannot connect: no auth token');
      setError('No auth token available');
      return;
    }

    isConnectingRef.current = true;
    console.log('[AgentChatSocket] Connecting to:', url.replace(/token=.*/, 'token=***'));

    const ws = new WebSocket(url);

    ws.onopen = () => {
      console.log('[AgentChatSocket] WebSocket opened, setting wsRef.current');
      setIsConnected(true);
      setError(null);
      
      // Check if this is a reconnection
      const isReconnection = wasConnectedRef.current;
      if (isReconnection) {
        console.log('[AgentChatSocket] Reconnected! Calling onReconnected callback');
        onReconnected?.(executingAtDisconnectRef.current.wasExecuting, executingAtDisconnectRef.current.executionId);
      }
      wasConnectedRef.current = true;
      reconnectAttemptsRef.current = 0;
      isConnectingRef.current = false;
    };

    ws.onclose = (event) => {
      console.log('[AgentChatSocket] Disconnected:', event.code, event.reason);
      
      // Store execution state at disconnect time for reconnection callback
      const hasRecoverableWork = hasRecoverableExecution();
      executingAtDisconnectRef.current = {
        wasExecuting: hasRecoverableWork,
        executionId: executionIdRef.current,
      };
      
      setIsConnected(false);
      isConnectingRef.current = false;
      wsRef.current = null;
      onDisconnected?.();

      // Auto-reconnect if not intentionally closed (code 1000 = normal close)
      // Keep trying while an execution is unresolved so missed terminal messages
      // can be reconciled from persisted state after the socket comes back.
      if (event.code !== 1000 && (reconnectAttemptsRef.current < maxReconnectAttempts || hasRecoverableWork)) {
        reconnectAttemptsRef.current++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        console.log(`[AgentChatSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        console.log('[AgentChatSocket] Max reconnect attempts reached, giving up');
        setError('Connection lost. Please refresh the page.');
      }
    };

    ws.onerror = (event) => {
      console.error('[AgentChatSocket] Error:', event);
      isConnectingRef.current = false;
    };

    // Use a wrapper that calls through the ref to avoid stale closures
    // This ensures the latest handleMessage is always called even if callbacks change
    ws.onmessage = (event) => {
      handleMessageRef.current?.(event);
    };

    wsRef.current = ws;
    console.log('[AgentChatSocket] wsRef.current set to WebSocket instance');
  }, [getWebSocketUrl, hasRecoverableExecution, onDisconnected, onReconnected]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('[AgentChatSocket] Disconnecting');
    reconnectAttemptsRef.current = maxReconnectAttempts; // Prevent auto-reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
    isConnectingRef.current = false;
  }, []);

  // Execute a message
  const execute = useCallback((message: string, execOptions?: {
    sessionId?: string | null;
    aiSystemId?: number;
    context?: Record<string, any>;
    maxIterations?: number;
  }) => {
    console.log('[AgentChatSocket] execute called, wsRef.current:', !!wsRef.current, 'readyState:', wsRef.current?.readyState, '(OPEN=1)');
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[AgentChatSocket] Not connected - wsRef.current:', !!wsRef.current, 'readyState:', wsRef.current?.readyState);
      onError?.('WebSocket not connected');
      return;
    }

    // Reset state for new execution
    executionIdRef.current = null;
    setExecutionId(null);
    setResult(null);
    setError(null);
    statusRef.current = 'RUNNING';
    setStatus('RUNNING');
    isExecutingRef.current = true;
    setIsExecuting(true);
    queuedTaskIdRef.current = null;
    setQueuedTaskId(null);
    setCurrentIteration(0);

    const payload: Record<string, any> = {
      type: 'execute',
      message,
    };

    if (execOptions?.sessionId) {
      payload.session_id = execOptions.sessionId;
    }
    if (execOptions?.aiSystemId) {
      payload.ai_system_id = execOptions.aiSystemId;
    }
    if (execOptions?.context) {
      payload.context = execOptions.context;
    }
    if (execOptions?.maxIterations !== undefined) {
      payload.max_iterations = execOptions.maxIterations;
    }

    console.log('[AgentChatSocket] Executing:', payload);
    wsRef.current.send(JSON.stringify(payload));
  }, [onError]);

  // Stop current execution
  // Stop execution - takes executionId as parameter to avoid stale closure issues
  const stop = useCallback((execId?: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[AgentChatSocket] stop - Not connected');
      return false;
    }

    const idToStop = execId || executionIdRef.current;
    const taskIdToStop = queuedTaskIdRef.current;
    if (!idToStop && !taskIdToStop) {
      console.error('[AgentChatSocket] No execution or queued task to stop');
      return false;
    }

    console.log('[AgentChatSocket] Stopping execution:', idToStop || taskIdToStop);
    const payload: Record<string, any> = {
      type: 'stop',
    };
    if (idToStop) {
      stopPendingExecutionIdRef.current = idToStop;
      payload.execution_id = idToStop;
    } else if (taskIdToStop) {
      stopPendingTaskIdRef.current = taskIdToStop;
      payload.task_id = taskIdToStop;
    }
    wsRef.current.send(JSON.stringify(payload));
    return true;
  }, []);

  // Reset state
  const reset = useCallback(() => {
    const previousExecutionId = executionIdRef.current || stopPendingExecutionIdRef.current;
    if (previousExecutionId) {
      ignoredExecutionIdsRef.current.add(previousExecutionId);
      stopPendingExecutionIdRef.current = previousExecutionId;
    }
    executionIdRef.current = null;
    setExecutionId(null);
    queuedTaskIdRef.current = null;
    setQueuedTaskId(null);
    sessionIdRef.current = null;
    setSessionId(null);
    setCurrentIteration(0);
    setMaxIterations(100);
    setStatus(null);
    setError(null);
    setResult(null);
    setTotalCost(0);
    setTotalTokens(0);
    setToolNames([]);
    statusRef.current = null;
    isExecutingRef.current = false;
    setIsExecuting(false);
    setPendingConfirmation(null);
  }, []);

  // Force stop execution state (for when WebSocket doesn't respond to stop)
  const forceStopState = useCallback(() => {
    console.log('[AgentChatSocket] Force stopping execution state');
    const activeExecutionId = executionIdRef.current || stopPendingExecutionIdRef.current;
    if (activeExecutionId) {
      terminalExecutionIdsRef.current.add(activeExecutionId);
      stopPendingExecutionIdRef.current = null;
    }
    stopPendingTaskIdRef.current = null;
    isExecutingRef.current = false;
    setIsExecuting(false);
    queuedTaskIdRef.current = null;
    setQueuedTaskId(null);
    statusRef.current = 'CANCELLED';
    setStatus('CANCELLED');
  }, []);

  // Restore execution state (for page reload scenarios)
  // NOTE: This only sets local state. The subscribe message is sent separately
  // from useAIBuilderExecution to avoid duplicate subscriptions.
  const restore = useCallback((execId: string, sessId: string | null) => {
    console.log('[AgentChatSocket] Restoring execution state:', execId, 'session:', sessId);
    executionIdRef.current = execId;
    setExecutionId(execId);
    queuedTaskIdRef.current = null;
    setQueuedTaskId(null);
    if (sessId) {
      sessionIdRef.current = sessId;
      setSessionId(sessId);
    }
    isExecutingRef.current = true;
    setIsExecuting(true);
    statusRef.current = 'RUNNING';
    setStatus('RUNNING');
    setError(null);
  }, []);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect && agentId) {
      // Reset reconnect counter on mount (handles React StrictMode double-mount)
      reconnectAttemptsRef.current = 0;
      connect();
    }

    return () => {
      // On unmount, just close the socket without blocking future reconnects
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
      setIsConnected(false);
      isConnectingRef.current = false;
    };
  }, [autoConnect, agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isConnected]);

  // Reset reconnect counter and connect (useful after max attempts reached)
  const resetAndConnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  // Send a raw message directly - doesn't use callbacks, accesses wsRef directly
  // This avoids stale closure issues
  const sendRaw = useCallback((payload: Record<string, any>): boolean => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[AgentChatSocket] sendRaw - Not connected');
      return false;
    }
    wsRef.current.send(JSON.stringify(payload));
    return true;
  }, []); // No dependencies - wsRef is stable

  // Confirm or deny a dangerous tool execution
  const confirmTool = useCallback((
    execId: string,
    toolName: string,
    confirmed: boolean,
    toolCallId?: string | null
  ): boolean => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[AgentChatSocket] confirmTool - Not connected');
      return false;
    }

    console.log('[AgentChatSocket] Confirming tool:', toolName, 'confirmed:', confirmed);
    const payload: Record<string, any> = {
      type: 'confirm_tool',
      execution_id: execId,
      tool_name: toolName,
      confirmed,
    };
    if (toolCallId) payload.tool_call_id = toolCallId;
    wsRef.current.send(JSON.stringify(payload));
    return true;
  }, []); // No dependencies - wsRef is stable

  return {
    isConnected,
    isExecuting,
    executionId,
    queuedTaskId,
    sessionId,
    currentIteration,
    maxIterations,
    status,
    error,
    result,
    totalCost,
    totalTokens,
    toolNames,
    wsRef,
    connect,
    disconnect,
    execute,
    stop,
    reset,
    forceStopState,
    restore,
    resetAndConnect,
    sendRaw,
    confirmTool,
    pendingConfirmation,
  };
}

export default useAgentChatSocket;
